import type { IndicatorSeries, Observation } from "../types.ts";

export type ShockIdentificationTier =
  | "observed_change_fallback"
  | "residual_innovation_proxy"
  | "market_event_proxy"
  | "externally_identified_series";

export type ShockCausalReadiness = "observational_only" | "event_proxy_only" | "externally_identified";

export interface PolicyMarketRepricingEvent {
  date: string;
  month: string;
  previousPolicyRate: number;
  policyRate: number;
  policyChangePp: number;
  previousMarketDate: string;
  previousMarketRate: number;
  marketRate: number;
  marketRepricingBp: number;
}

export interface PolicyMarketRepricingProxy {
  status: "ready" | "limited" | "unavailable";
  method: "market_event_proxy";
  causalReadiness: "event_proxy_only";
  policySeriesId: string;
  marketSeriesId: string;
  detectedPolicyChanges: number;
  matchedEvents: number;
  unmatchedPolicyChangeDates: string[];
  commonStartDate: string | null;
  commonEndDate: string | null;
  events: PolicyMarketRepricingEvent[];
  monthlySeries: IndicatorSeries;
  note: string;
}

export interface ExternalShockObservation {
  date: string;
  value: number;
  availableAt: string;
}

export interface ExternalShockDefinition {
  id: string;
  label: string;
  provider: string;
  sourceUrl: string;
  method: string;
  unit: "%" | "bp" | "index" | "pp";
  frequency: "monthly" | "event";
  verifiedExternal: boolean;
  pointInTimeSafe: boolean;
  zeroMonthsVerified?: boolean;
  observations: ExternalShockObservation[];
  note?: string;
}

export interface ExternalShockRegistryPayload {
  schemaVersion: number;
  updatedAt?: string | null;
  shocks?: ExternalShockDefinition[];
}

export interface ExternalShockValidation {
  id: string;
  label: string;
  eligible: boolean;
  monthlyLpReady: boolean;
  causalReadiness: "externally_identified" | "observational_only";
  errors: string[];
  definition: ExternalShockDefinition;
  series: IndicatorSeries | null;
}

export interface ExternalShockRegistry {
  status: "ready" | "unavailable" | "invalid";
  schemaVersion: number | null;
  updatedAt: string | null;
  validations: ExternalShockValidation[];
  eligible: ExternalShockValidation[];
  note: string;
}

function dateMs(date: string) {
  const value = Date.parse(date);
  return Number.isFinite(value) ? value : null;
}

function numericDaily(series: IndicatorSeries) {
  return series.observations
    .filter((obs): obs is Observation & { value: number } => obs.value != null && Number.isFinite(obs.value) && /^\d{4}-\d{2}-\d{2}$/.test(obs.date))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function previousWithin(entries: Array<{ date: string; value: number }>, date: string, maxCalendarDays: number) {
  const targetMs = dateMs(date);
  if (targetMs == null) return null;
  for (let i = entries.length - 1; i >= 0; i--) {
    const item = entries[i];
    if (item.date >= date) continue;
    const itemMs = dateMs(item.date);
    if (itemMs == null) continue;
    const days = (targetMs - itemMs) / 86_400_000;
    if (days > maxCalendarDays) return null;
    return item;
  }
  return null;
}

function shiftMonth(month: string, delta: number) {
  const [year, mon] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, mon - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthRange(start: string, end: string) {
  const out: string[] = [];
  let cursor = start;
  while (cursor <= end && out.length < 5000) {
    out.push(cursor);
    cursor = shiftMonth(cursor, 1);
  }
  return out;
}

export function buildPolicyChangeMarketRepricingProxy(args: {
  policyRateDaily?: IndicatorSeries | null;
  marketRateDaily?: IndicatorSeries | null;
}): PolicyMarketRepricingProxy {
  const policy = args.policyRateDaily;
  const market = args.marketRateDaily;
  const unavailableSeries: IndicatorSeries = {
    id: "bok_policy_change_ktb1_repricing_proxy",
    name: "기준금리 변경일 국고채1Y 재평가 proxy",
    shortName: "BOK event proxy",
    unit: "bp",
    layer: "policy",
    source: {
      provider: "Calculated from 한국은행 ECOS",
      dataset: "BOK policy-change-day KTB1Y repricing proxy",
      url: market?.source.url ?? policy?.source.url ?? "",
      frequency: "monthly",
      quality: "unavailable",
      note: "정책금리 변경일과 같은 날의 국고채1Y 관측이 있어야 계산합니다."
    },
    observations: []
  };
  if (!policy || !market) return {
    status: "unavailable", method: "market_event_proxy", causalReadiness: "event_proxy_only",
    policySeriesId: policy?.id ?? "unavailable", marketSeriesId: market?.id ?? "unavailable",
    detectedPolicyChanges: 0, matchedEvents: 0, unmatchedPolicyChangeDates: [], commonStartDate: null, commonEndDate: null,
    events: [], monthlySeries: unavailableSeries,
    note: "정책금리 또는 국고채1Y 일별 시계열이 없어 event proxy를 계산하지 못했습니다."
  };
  const policyObs = numericDaily(policy);
  const marketObs = numericDaily(market);
  if (policyObs.length < 2 || marketObs.length < 2) return {
    status: "unavailable", method: "market_event_proxy", causalReadiness: "event_proxy_only",
    policySeriesId: policy.id, marketSeriesId: market.id, detectedPolicyChanges: 0, matchedEvents: 0,
    unmatchedPolicyChangeDates: [], commonStartDate: null, commonEndDate: null, events: [], monthlySeries: unavailableSeries,
    note: "정책금리 또는 국고채1Y 일별 숫자 관측이 부족합니다."
  };
  const marketByDate = new Map(marketObs.map((obs) => [obs.date, obs.value]));
  const commonStartDate = [policyObs[0].date, marketObs[0].date].sort().at(-1)!;
  const commonEndDate = [policyObs.at(-1)!.date, marketObs.at(-1)!.date].sort().at(0)!;
  const changes: Array<{ date: string; previous: number; current: number }> = [];
  let previous: number | null = null;
  for (const obs of policyObs) {
    if (obs.date < commonStartDate || obs.date > commonEndDate) { previous = obs.value; continue; }
    if (previous != null && Math.abs(obs.value - previous) > 1e-12) changes.push({ date: obs.date, previous, current: obs.value });
    previous = obs.value;
  }
  const events: PolicyMarketRepricingEvent[] = [];
  const unmatched: string[] = [];
  for (const change of changes) {
    const currentMarket = marketByDate.get(change.date);
    const priorMarket = previousWithin(marketObs, change.date, 7);
    if (currentMarket == null || !priorMarket) { unmatched.push(change.date); continue; }
    events.push({
      date: change.date,
      month: change.date.slice(0, 7),
      previousPolicyRate: change.previous,
      policyRate: change.current,
      policyChangePp: change.current - change.previous,
      previousMarketDate: priorMarket.date,
      previousMarketRate: priorMarket.value,
      marketRate: currentMarket,
      marketRepricingBp: (currentMarket - priorMarket.value) * 100
    });
  }
  const startMonth = commonStartDate.slice(0, 7);
  const endMonth = commonEndDate.slice(0, 7);
  const byMonth = new Map<string, number>();
  for (const event of events) byMonth.set(event.month, (byMonth.get(event.month) ?? 0) + event.marketRepricingBp);
  // For this internally defined event variable, months without a policy-rate change are
  // genuinely zero events by construction. This zero-fill rule does NOT apply to external shocks.
  const observations = monthRange(startMonth, endMonth).map((month) => ({ date: `${month}-01`, value: byMonth.get(month) ?? 0 }));
  const monthlySeries: IndicatorSeries = {
    id: "bok_policy_change_ktb1_repricing_proxy",
    name: "기준금리 변경일 국고채1Y 재평가 proxy",
    shortName: "BOK event proxy",
    unit: "bp",
    layer: "policy",
    source: {
      provider: "Calculated from 한국은행 ECOS",
      dataset: "BOK policy-change-day KTB1Y same-day repricing",
      url: market.source.url,
      frequency: "monthly",
      quality: events.length >= 8 ? "official" : "stale",
      observationDate: observations.at(-1)?.date,
      note: "정책금리가 실제 변경된 날의 KTB1Y와 직전 7일 이내 거래일 관측 차이(bp). 동결회의 surprise·사전 기대·동시 뉴스는 분리하지 못하므로 외생 shock가 아닙니다.",
      sourceIds: [policy.id, market.id]
    },
    observations
  };
  return {
    status: events.length >= 8 ? (unmatched.length ? "limited" : "ready") : events.length ? "limited" : "unavailable",
    method: "market_event_proxy",
    causalReadiness: "event_proxy_only",
    policySeriesId: policy.id,
    marketSeriesId: market.id,
    detectedPolicyChanges: changes.length,
    matchedEvents: events.length,
    unmatchedPolicyChangeDates: unmatched,
    commonStartDate,
    commonEndDate,
    events,
    monthlySeries,
    note: "같은 날 국고채 재평가는 정책결정의 시장 재평가 proxy일 뿐 고빈도 surprise가 아닙니다. 정책이 이미 예상됐거나 같은 날 다른 뉴스가 있으면 오염될 수 있습니다."
  };
}

function validUrl(value: string) {
  try { const url = new URL(value); return url.protocol === "https:" || url.protocol === "http:"; } catch { return false; }
}

function consecutiveMonths(observations: ExternalShockObservation[]) {
  const months = [...new Set(observations.map((obs) => obs.date.slice(0, 7)))].sort();
  if (months.length < 2) return months.length === 1;
  return months.every((month, index) => index === 0 || month === shiftMonth(months[index - 1], 1));
}

function buildExternalSeries(definition: ExternalShockDefinition): IndicatorSeries | null {
  const sorted = [...definition.observations].sort((a, b) => a.date.localeCompare(b.date));
  if (!sorted.length) return null;
  let observations: Observation[];
  if (definition.frequency === "monthly") {
    observations = sorted.map((obs) => ({ date: `${obs.date.slice(0, 7)}-01`, value: obs.value }));
  } else if (definition.zeroMonthsVerified) {
    const start = sorted[0].date.slice(0, 7);
    const end = sorted.at(-1)!.date.slice(0, 7);
    const monthly = new Map<string, number>();
    for (const obs of sorted) monthly.set(obs.date.slice(0, 7), (monthly.get(obs.date.slice(0, 7)) ?? 0) + obs.value);
    observations = monthRange(start, end).map((month) => ({ date: `${month}-01`, value: monthly.get(month) ?? 0 }));
  } else {
    return null;
  }
  return {
    id: definition.id,
    name: definition.label,
    shortName: definition.label,
    unit: definition.unit,
    layer: "policy",
    source: {
      provider: definition.provider,
      dataset: definition.method,
      url: definition.sourceUrl,
      frequency: "monthly",
      quality: "official",
      observationDate: observations.at(-1)?.date,
      releaseDate: sorted.at(-1)?.availableAt,
      note: [definition.note, "Externally identified series passed FREM v17.2 strict source/PIT gate."].filter(Boolean).join(" ")
    },
    observations
  };
}

export function validateExternalShockRegistry(payload: unknown): ExternalShockRegistry {
  if (!payload || typeof payload !== "object") return { status: "invalid", schemaVersion: null, updatedAt: null, validations: [], eligible: [], note: "external shock registry payload가 객체가 아닙니다." };
  const raw = payload as ExternalShockRegistryPayload;
  if (raw.schemaVersion !== 1 || !Array.isArray(raw.shocks)) return { status: "invalid", schemaVersion: typeof raw.schemaVersion === "number" ? raw.schemaVersion : null, updatedAt: raw.updatedAt ?? null, validations: [], eligible: [], note: "schemaVersion=1과 shocks 배열이 필요합니다." };
  const validations = raw.shocks.map((definition): ExternalShockValidation => {
    const errors: string[] = [];
    if (!definition || typeof definition !== "object") return { id: "invalid", label: "invalid", eligible: false, monthlyLpReady: false, causalReadiness: "observational_only", errors: ["shock definition이 객체가 아닙니다."], definition: definition as ExternalShockDefinition, series: null };
    if (!definition.id?.trim()) errors.push("id 없음");
    if (!definition.label?.trim()) errors.push("label 없음");
    if (!definition.provider?.trim()) errors.push("provider 없음");
    if (!validUrl(definition.sourceUrl ?? "")) errors.push("유효한 sourceUrl 없음");
    if (!definition.method?.trim()) errors.push("method 없음");
    if (definition.verifiedExternal !== true) errors.push("verifiedExternal=true 필요");
    if (definition.pointInTimeSafe !== true) errors.push("pointInTimeSafe=true 필요");
    if (!Array.isArray(definition.observations) || definition.observations.length < 8) errors.push("숫자관측 최소 8건 필요");
    for (const observation of definition.observations ?? []) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(observation.date ?? "") || !Number.isFinite(observation.value) || !/^\d{4}-\d{2}-\d{2}/.test(observation.availableAt ?? "")) {
        errors.push("모든 관측에 유효한 date/value/availableAt 필요"); break;
      }
      const obsMs = dateMs(observation.date); const availMs = dateMs(observation.availableAt);
      if (obsMs == null || availMs == null || availMs < obsMs) { errors.push("availableAt은 observation date보다 빠를 수 없음"); break; }
    }
    if (definition.frequency !== "monthly" && definition.frequency !== "event") errors.push("frequency는 monthly 또는 event");
    if (definition.frequency === "event" && definition.zeroMonthsVerified !== true) errors.push("event shock의 월간 LP에는 zeroMonthsVerified=true 필요");
    if (definition.frequency === "monthly" && Array.isArray(definition.observations) && definition.observations.length >= 8 && !consecutiveMonths(definition.observations)) errors.push("monthly shock 관측은 연속월이어야 함");
    const eligible = errors.length === 0;
    const series = eligible ? buildExternalSeries(definition) : null;
    return {
      id: definition.id ?? "invalid",
      label: definition.label ?? definition.id ?? "invalid",
      eligible,
      monthlyLpReady: eligible && series != null,
      causalReadiness: eligible ? "externally_identified" : "observational_only",
      errors,
      definition,
      series
    };
  });
  const eligible = validations.filter((item) => item.eligible && item.monthlyLpReady);
  return {
    status: raw.shocks.length === 0 ? "unavailable" : validations.some((item) => !item.eligible) ? "invalid" : "ready",
    schemaVersion: 1,
    updatedAt: raw.updatedAt ?? null,
    validations,
    eligible,
    note: eligible.length
      ? `${eligible.length}개 외부 shock가 source/PIT/monthly-LP strict gate를 통과했습니다.`
      : "기본 배포본에는 검증 완료된 외부 surprise shock가 없습니다. 기존 분석은 causal로 자동 승격되지 않습니다."
  };
}

export async function loadExternalShockRegistry(signal?: AbortSignal): Promise<ExternalShockRegistry> {
  void signal;
  return validateExternalShockRegistry({ schemaVersion: 1, updatedAt: null, shocks: [] });
}
